import { useState } from "react";
import { MOCK } from "./MockData";
import { fmt, statusColor } from "./Utils/Utilities";
import { RevenueChart, CollectionBarChart, BarChart, Badge } from "./SharedUI";
import { exportReportPdf, printReport } from "./Utils/pdf";
import RentalAgingPage     from "./Rentalaging";
import AllocationHistoryPage from "./Allocationhistory";
import OrderHistoryPage    from "./orderhistory";
import { MOCK_RENTALS } from "./Rentalaging";
import { MOCK_ALLOCATIONS } from "./Allocationhistory";
import { MOCK_ORDER_HISTORY } from "./orderhistory";
  
// ─── Constants ────────────────────────────────────────────────────────────────
const REPORT_TYPES = ["Expense",  "Orders", "Inventory"];
const PERIODS      = ["2026", "2025", "Q1 2026", "Q4 2025"];

const EXPENSE_KPIS = [
  { label: "Total Expense", value: "R2.85M", trend: "+12.4%", up: true,  note: "vs prior year" },
  { label: "Avg Monthly",   value: "R237k",  trend: "+8.1%",  up: true,  note: "rolling 6 months" },
  { label: "Current Month",    value: "R354k",  trend: "2026/05/07",up: true,  note: "all-time high" },
];

const ORDER_STATUSES = [
  "Delivered", "In Transit", "Pending", "Confirmed", "Processing", "Cancelled",
];

// ─── Shared sub-components ────────────────────────────────────────────────────

function KPICard({ label, value, trend, up, note }) {
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--border)",
      borderTop: `2px solid ${up ? "var(--success)" : "var(--danger)"}`,
      borderRadius: "var(--r12)", padding: "16px 18px",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "inherit", color: "var(--text1)", lineHeight: 1, marginBottom: 6 }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: up ? "var(--success)" : "var(--danger)" }}>{trend}</span>
        {note && <span style={{ fontSize: 10, color: "var(--text3)" }}>{note}</span>}
      </div>
    </div>
  );
}

function ChartPanel({ title, subtitle, children, action }) {
  return (
    <div className="panel">
      <div className="ph">
        <div style={{ flex: 1 }}>
          <div className="ph-title">{title}</div>
          {subtitle && <div className="ph-sub">{subtitle}</div>}
        </div>
        {action}
      </div>
      <div className="pb">{children}</div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text2)" }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

// ─── Drill-down card ──────────────────────────────────────────────────────────
// Cards shown on the main reporting page that navigate to sub-detail pages.
function DrillCard({ icon, title, desc, accent, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderTop: `2px solid ${accent}`, borderRadius: 12,
        padding: "16px 18px", cursor: "pointer",
        transition: "border-color .15s, transform .15s",
        display: "flex", alignItems: "center", gap: 14,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = accent; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = "var(--border)"; }}
    >
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${accent}18`, border: `1px solid ${accent}35`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div className="fs13 fw6">{title}</div>
        <div className="fs10 tc3" style={{ marginTop: 2 }}>{desc}</div>
      </div>
      <i className="bi bi-arrow-right" style={{ color: accent, fontSize: 13 }} />
    </div>
  );
}

// ─── Expense tab ──────────────────────────────────────────────────────────────
function ExpenseReport({ period }) {
  const BAR_COLORS = ["var(--flame)", "var(--amber)", "var(--blue)", "var(--teal)", "var(--success)"];
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {EXPENSE_KPIS.map((k) => <KPICard key={k.label} {...k} />)}
      </div>

      {/* Small snapshots replacing the larger revenue panels */}
      <div className="g2" style={{ marginTop: 16 }}>
        <ChartPanel title="Rental Aging Snapshot" subtitle="Quick rental exposure preview">
                  <div style={{ padding: 8 }}>
                    <div className="fs11 fw6" style={{ marginBottom: 8 }}>Overdue rentals: {MOCK_RENTALS.filter(r => r.status === "Overdue").length}</div>
                    {MOCK_RENTALS.filter(r => r.status === "Overdue").slice(0,3).map(r => (
                      <div key={`${r.asset}-${r.employeeId}`} className="fb" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                        <div style={{ flex: 1 }}>
                          <div className="fs11 fw6">{r.asset} · {r.employee} ({r.employeeId})</div>
                          <div className="fs10 tc3">{r.daysOut} days · {r.site}</div>
                        </div>
                        <div className="mono fw7">{r.daysOut}d</div>
                      </div>
                    ))}
                  </div>
        </ChartPanel>

        {/* <ChartPanel title="Allocation Snapshot" subtitle="Recent allocations & depot mix">
          <div style={{ padding: 8 }}>
            {MOCK_ALLOCATIONS.slice(0,3).map(a => (
              <div key={a.id} className="fb" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ flex: 1 }}>
                  <div className="fs11 fw6">{a.product} · {a.employee}</div>
                  <div className="fs10 tc3">{a.qty} {a.unit} · {a.site}</div>
                </div>
                <div className="fs10" style={{ color: a.status === "Fulfilled" ? "var(--success)" : "var(--amber)" }}>{a.status}</div>
              </div>
            ))}
          </div>
        </ChartPanel>*/}

        <ChartPanel title="Order History Snapshot" subtitle="Recent order events">
          <div style={{ padding: 8 }}>
            {MOCK_ORDER_HISTORY.slice(0,3).map(o => (
              <div key={o.id} className="fb" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ flex: 1 }}>
                  <div className="fs11 fw6">{o.id} · {o.customer}</div>
                  <div className="fs10 tc3">{o.product} · {fmt.currency(o.total)}</div>
                </div>
                <div><Badge status={o.status} /></div>
              </div>
            ))}
          </div>
        </ChartPanel>
      </div>
    </>
  );
}

// ─── Orders tab ───────────────────────────────────────────────────────────────
function OrdersReport() {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {ORDER_STATUSES.map((s) => {
          const col   = statusColor(s);
          const items = MOCK.orders.filter(o => o.status === s);
          return (
            <div key={s} style={{ background: "var(--surface)", border: `1px solid ${col}30`, borderTop: `2px solid ${col}`, borderRadius: "var(--r12)", padding: "14px 16px" }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: col, marginBottom: 8 }}>{s}</div>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "inherit", color: "var(--text1)", lineHeight: 1, marginBottom: 4 }}>{items.length}</div>
              <div className="mono fs10" style={{ color: "var(--text3)" }}>{fmt.currency(items.reduce((a, o) => a + o.total, 0))}</div>
            </div>
          );
        })}
      </div>
      <div className="panel">
        <div className="ph">
          <div className="ph-title">Orders Summary</div>
          <div className="ph-sub">{MOCK.orders.length} total orders</div>
        </div>
        <table className="tbl">
          <thead>
            <tr><th>Order ID</th><th>Customer</th><th>Product</th><th>Volume</th><th>Total</th><th>Region</th><th>Status</th></tr>
          </thead>
          <tbody>
            {MOCK.orders.map((o) => (
              <tr key={o.id}>
                <td><span className="mono t-flame" style={{ fontWeight: 700, fontSize: 11 }}>{o.id}</span></td>
                <td style={{ fontWeight: 600, fontSize: 12 }}>{o.customer}</td>
                <td className="fs11" style={{ color: "var(--text2)" }}>{o.product}</td>
                <td className="mono fs11" style={{ color: "var(--text2)" }}>{fmt.number(o.qty)} {o.unit}</td>
                <td><span className="mono" style={{ fontWeight: 700, fontSize: 12 }}>{fmt.currency(o.total)}</span></td>
                <td><span className="tag">{o.region}</span></td>
                <td><Badge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Inventory tab ────────────────────────────────────────────────────────────
function InventoryReport() {
  return (
    <div className="panel">
      <div className="ph">
        <div className="ph-title">Inventory Levels</div>
        <div className="ph-sub">
          {MOCK.inventory.filter(i => i.status === "Critical").length} critical ·{" "}
          {MOCK.inventory.filter(i => i.status === "Low").length} low
        </div>
      </div>
      <div className="pb">
        {MOCK.inventory.map((item) => {
          const pct  = Math.round((item.stock / item.maxStock) * 100);
          const fill = item.status === "Critical" ? "var(--danger)" : item.status === "Low" ? "var(--warn)" : "var(--teal)";
          return (
            <div key={item.id} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{item.product}</span>
                  <span className="mono fs10" style={{ color: "var(--text3)", marginLeft: 8 }}>{item.sku}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="mono fs11" style={{ color: "var(--text2)" }}>{fmt.number(item.stock)} / {fmt.number(item.maxStock)} {item.unit}</span>
                  <Badge status={item.status} />
                </div>
              </div>
              <div className="prog-wrap" style={{ height: 7 }}>
                <div className="prog-fill" style={{ width: `${pct}%`, background: fill }} />
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 10, color: "var(--text3)" }}>
                <span>{pct}% capacity</span>
                <span>Min: {fmt.number(item.minStock)} {item.unit}</span>
                <span>Location: {item.location}</span>
                <span>Supplier: {item.supplier}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────
function ReportingPage({ showToast: toast }) {
  const [reportType, setReportType] = useState("Expense");
  const [period,     setPeriod]     = useState("2026");
  // sub-page routing: null | "rental-aging" | "allocation-history" | "order-history"
  const [subPage,    setSubPage]    = useState(null);

  // render sub-pages fullscreen (same pattern as AccountDetailPage)
  if (subPage === "rental-aging")
    return <RentalAgingPage showToast={toast} onBack={() => setSubPage(null)} />;
  if (subPage === "allocation-history")
    return <AllocationHistoryPage showToast={toast} onBack={() => setSubPage(null)} />;
  if (subPage === "order-history")
    return <OrderHistoryPage showToast={toast} onBack={() => setSubPage(null)} />;

  const handleExport = () => {
    const dataMap = { Revenue: MOCK.revenueChart, Orders: MOCK.orders, Inventory: MOCK.inventory };
    exportReportPdf(reportType, dataMap[reportType], period, `reports-${reportType.toLowerCase()}-${period}.pdf`);
  };

  const handlePrint = () => {
    const dataMap = { Revenue: MOCK.revenueChart, Orders: MOCK.orders, Inventory: MOCK.inventory };
    printReport(reportType, dataMap[reportType], period);
  };

  return (
    <div className="page">
      {/* ── Top bar ── */}
      <div className="fb mb22">
        <div className="tabs">
          {REPORT_TYPES.map((r) => (
            <div key={r} className={`tab ${reportType === r ? "on" : ""}`} onClick={() => setReportType(r)}>{r}</div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select className="tfilter" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <button className="btn btn-s" onClick={handlePrint}><i className="bi bi-printer" /> Print</button>
          <button className="btn btn-s" onClick={handleExport}><i className="bi bi-file-earmark-pdf" /> Export PDF</button>
        </div>
      </div>

      {/* ── Active report ── */}
      {reportType === "Expense"     && <ExpenseReport     period={period} />}
      {reportType === "Orders"      && <OrdersReport      period={period} />}
      {reportType === "Inventory"   && <InventoryReport   period={period} />}

      {/* ── Drill-down section (always visible at bottom) ── */}
      <div style={{ marginTop: 24 }}>
        <div className="fs10 fw7 tc3" style={{ textTransform: "uppercase", letterSpacing: ".12em", marginBottom: 12 }}>Detailed Reports</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          <DrillCard
            icon="🛢️"
            title="Rental Aging"
            desc="Cylinder & equipment rental exposure by aging bucket"
            accent="var(--amber)"
            onClick={() => setSubPage("rental-aging")}
          />
          {/*<DrillCard
            icon="📦"
            title="Allocation History"
            desc="Stock reservations per order, depot source & fulfilment status"
            accent="var(--blue)"
            onClick={() => setSubPage("allocation-history")}
          />*/}
          <DrillCard
            icon="🚛"
            title="Order History"
            desc="Full order log with delivery timeline, driver & invoice linkage"
            accent="var(--teal)"
            onClick={() => setSubPage("order-history")}
          />
        </div>
      </div>
    </div>
  );
}

export default ReportingPage;